import type { AdminAuthenticationResponse, ApiError } from "@repo/types"
import type { FetchError, FetchOptions, ResponseType } from "ofetch"
import { ofetch } from "ofetch"
import { env } from "@/shared/config"
import { currentLocale } from "@/shared/config/i18n"
import { clearTokens, getAccessToken, getRefreshToken, setTokens } from "./tokens"

// env.VITE_API_PREFIX is a same-origin path (default "/api") because the backend
// sends no CORS headers: the Vite proxy forwards it in dev, nginx in the
// container. The backend already namespaces its routes under /api, so paths
// passed here start at the controller (e.g. "/Auth/Login").
const baseFetch = ofetch.create({
  baseURL: env.VITE_API_PREFIX,
  timeout: env.VITE_API_TIMEOUT,
  retry: false,
})

export interface HttpOptions extends FetchOptions {
  /** Set false for the anonymous endpoints (login, token refresh). */
  auth?: boolean
}

// The app registers what should happen when a session is definitively gone —
// keeps this module free of store/router imports, and makes it unit-testable.
let onSessionExpired: (() => void) | null = null

export function setSessionExpiredHandler(handler: () => void): void {
  onSessionExpired = handler
}

export function isUnauthorized(error: unknown): boolean {
  return (error as FetchError)?.response?.status === 401
}

export function isNotFound(error: unknown): boolean {
  return (error as FetchError)?.response?.status === 404
}

/**
 * Pulls the human-readable part out of an ASP.NET ProblemDetails body. Returns
 * `undefined` when the body carries nothing usable, so the caller can fall back
 * to its own translated copy instead of showing a server-shaped string.
 *
 * `title` is deliberately not part of the chain: on a 500 it is the framework's
 * own English "An error occurred while processing your request.", which reads
 * worse to an Uzbek admin than our translated fallback.
 */
export function apiErrorMessage(error: unknown): string | undefined {
  const data = (error as FetchError<ApiError>)?.data
  const fieldError = data?.errors && Object.values(data.errors).flat()[0]
  return (data?.detail ?? fieldError)?.trim() || undefined
}

// The caller's `T` already names the body (`Blob` for `responseType: "blob"`),
// so ofetch's per-responseType union is narrowed back to it.
function send<T>(url: string, options: FetchOptions, auth: boolean): Promise<T> {
  const token = auth ? getAccessToken() : null
  return baseFetch<T, ResponseType>(url, {
    ...options,
    headers: {
      // Reference-data labels (e.g. subscription statuses) are translated server-side.
      "Accept-Language": currentLocale(),
      ...options.headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  }) as Promise<T>
}

// One refresh at a time: a page that fires several requests at once must not
// spend its single-use refresh token more than once.
let refreshInFlight: Promise<boolean> | null = null

async function rotateTokens(): Promise<boolean> {
  const refreshToken = getRefreshToken()
  if (!refreshToken)
    return false

  try {
    const auth = await baseFetch<AdminAuthenticationResponse>("/Auth/RefreshToken", {
      method: "POST",
      body: { refreshToken },
    })
    if (!auth.accessToken || !auth.refreshToken)
      return false
    setTokens({ accessToken: auth.accessToken, refreshToken: auth.refreshToken })
    return true
  } catch {
    return false
  }
}

function refreshOnce(): Promise<boolean> {
  refreshInFlight ||= rotateTokens().finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

/**
 * Authenticated request. On 401 it rotates the token pair once and replays the
 * call; if the refresh itself fails the session is dropped and the original
 * error is rethrown so the caller still sees why the request failed.
 */
export async function http<T>(url: string, options: HttpOptions = {}): Promise<T> {
  const { auth = true, ...rest } = options

  try {
    return await send<T>(url, rest, auth)
  } catch (error) {
    if (!auth || !isUnauthorized(error))
      throw error

    if (await refreshOnce())
      return await send<T>(url, rest, true)

    clearTokens()
    onSessionExpired?.()
    throw error
  }
}

/** Binary download (e.g. an app icon) through the same token and refresh handling. */
export function httpBlob(url: string): Promise<Blob> {
  return http<Blob>(url, { responseType: "blob" })
}

/**
 * The backend hands out file URLs with its own `/api` namespace; `http` paths
 * start at the controller, so the prefix comes off. Anything else (another host,
 * a protocol-relative URL) is refused: `http` would send it the bearer token.
 */
export function apiPath(url: string): string | null {
  return /^\/api\/(?!\/)/.test(url) ? url.slice("/api".length) : null
}
